/**
 * SkillTreeScene.ts — 3-tab skill tree visualization + allocation UI.
 *
 * Shows 3 branches per class with 5 skills each (tier 1->2->3).
 * Players spend stat points on skills, with prereq gates and max levels.
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { MenuOption } from '@ui/MenuSystem';
import type { GameState } from '@core/engine/GameState';
import type { HintTriggerType } from '@ui/HintManager';
import { getBranchesForClass, getSkill as lookupSkill, type SkillBranch, type SkillDefinition } from '@game/data/skills';
import { getClass } from '@game/data/classes';

export class SkillTreeScene implements Scene {
  name = 'SkillTreeScene';

  async enter(engine: GameEngine): Promise<void> {
    const { gameState } = engine.getContext();
    if (!gameState) {
      await engine.popScene();
      return;
    }

    await this.branchSelect(engine, gameState);
  }

  async exit(engine: GameEngine): Promise<void> {
    engine.getContext().sprites.hide();
  }

  // ─── Branch Selection ───────────────────────────────────

  private async branchSelect(engine: GameEngine, state: GameState): Promise<void> {
    const ctx = engine.getContext();
    const { menu, frameRenderer, terminalFrame, hintManager, effects } = ctx;
    const branches = getBranchesForClass(state.character.classId);
    const cls = getClass(state.character.classId);

    // Update terminal frame
    terminalFrame.updateInfo({
      chapter: 'CHARACTER',
      location: 'Skill Trees',
    });

    // Trigger skill tree hint on first visit
    await hintManager.maybeShowHint('first_skill_tree' as HintTriggerType);

    while (true) {
      ctx.sprites.hide();
      const pointsAvailable = state.character.unspentStatPoints;
      const pointsInvested = ctx.skillSystem.getPointsInvested(state);
      const footer = `Points: ${pointsAvailable} available  |  ${pointsInvested} invested`;

      const layout = frameRenderer.drawFrame({
        title: 'SKILL TREES',
        headerRight: cls.name,
        footer,
        borderColor: 'frostBlue',
      });

      const writer = frameRenderer.createWriter(layout.content, effects);
      writer.blankLine();
      writer.writeLine(`  Available points: ${pointsAvailable}`, pointsAvailable > 0 ? 'green' : 'frostSilver');
      writer.writeLine(`  Points in skills: ${pointsInvested}`, 'frostSilver');
      writer.blankLine();

      const options: MenuOption[] = branches.map(branch => {
        const invested = ctx.skillSystem.getPointsInBranch(state.character.classId, branch.name, state);
        return {
          label: branch.name,
          color: 'amber' as const,
          suffix: `(${invested} pts)`,
          suffixColor: 'yellow' as const,
        };
      });

      const menuCfg = writer.menuConfig();
      const sel = await menu.show(options, {
        showExit: true,
        exitLabel: 'Back',
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
        onBeforeRender: () => {
          ctx.sprites.hide();
          frameRenderer.drawFrame({
            title: 'SKILL TREES',
            headerRight: cls.name,
            footer,
            borderColor: 'frostBlue',
          });
        },
      });

      if (sel === -1) {
        await engine.popScene();
        return;
      }

      if (sel >= 0 && sel < branches.length) {
        await this.showBranch(engine, state, branches[sel]);
      }
    }
  }

  // ─── Branch Detail ──────────────────────────────────────

  private async showBranch(engine: GameEngine, state: GameState, branch: SkillBranch): Promise<void> {
    const ctx = engine.getContext();
    const { menu, effects, frameRenderer } = ctx;

    while (true) {
      ctx.sprites.hide();
      const footer = `Available points: ${state.character.unspentStatPoints}  |  Branch: ${branch.name}`;

      const layout = frameRenderer.drawFrame({
        title: branch.name,
        headerRight: `Points: ${state.character.unspentStatPoints}`,
        footer,
        borderColor: 'frostBlue',
      });

      const writer = frameRenderer.createWriter(layout.content, effects);
      writer.blankLine();

      // Display skills grouped by tier
      const tiers = [1, 2, 3] as const;
      const skillOptions: MenuOption[] = [];
      const skillMap: SkillDefinition[] = [];

      for (const tier of tiers) {
        const tierSkills = branch.skills.filter(s => s.tier === tier);
        const tierLabel = tier === 1 ? 'Entry' : tier === 2 ? 'Mid' : 'Capstone';
        writer.writeLine(`  Tier ${tier} (${tierLabel})`, 'yellow');

        for (const skill of tierSkills) {
          const level = state.skillLevels.get(skill.id) ?? 0;
          const canAlloc = ctx.skillSystem.canAllocate(skill.id, state);
          const meetsPrereqs = ctx.skillSystem.meetsPrereqs(skill, state);

          // Build level indicator
          const levelBar = this.buildLevelBar(level, skill.maxLevel);

          // Determine color
          let color: 'green' | 'frostBlue' | 'gray' | 'white' = 'white';
          if (level >= skill.maxLevel) color = 'green';
          else if (canAlloc) color = 'frostBlue';
          else if (!meetsPrereqs) color = 'gray';

          skillOptions.push({
            label: `${skill.name} ${levelBar}`,
            color,
            suffix: canAlloc ? '[+]' : '',
            suffixColor: 'green' as const,
          });
          skillMap.push(skill);
        }
      }

      writer.blankLine();

      const menuCfg = writer.menuConfig();
      const sel = await menu.show(skillOptions, {
        showExit: true,
        exitLabel: 'Back',
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
        onBeforeRender: () => {
          ctx.sprites.hide();
          frameRenderer.drawFrame({
            title: branch.name,
            headerRight: `Points: ${state.character.unspentStatPoints}`,
            footer,
            borderColor: 'frostBlue',
          });
        },
      });

      if (sel === -1) return;

      if (sel >= 0 && sel < skillMap.length) {
        const skill = skillMap[sel];
        await this.showSkillDetail(engine, state, skill);
      }
    }
  }

  // ─── Skill Detail ───────────────────────────────────────

  private async showSkillDetail(
    engine: GameEngine,
    state: GameState,
    skill: SkillDefinition,
  ): Promise<void> {
    const ctx = engine.getContext();
    const { menu, effects, frameRenderer } = ctx;
    const level = state.skillLevels.get(skill.id) ?? 0;
    const canAlloc = ctx.skillSystem.canAllocate(skill.id, state);

    ctx.sprites.hide();
    const layout = frameRenderer.drawFrame({
      title: skill.name,
      headerRight: `${level}/${skill.maxLevel}`,
      borderColor: 'frostBlue',
    });

    const writer = frameRenderer.createWriter(layout.content, effects);
    writer.blankLine();
    writer.writeLine(`  ${skill.description}`, 'white');
    writer.blankLine();
    writer.writeLine(`  Level: ${level}/${skill.maxLevel}  ${this.buildLevelBar(level, skill.maxLevel)}`, 'frostSilver');
    writer.writeLine(`  Tier: ${skill.tier}  |  Branch: ${skill.branch}`, 'frostSilver');
    writer.blankLine();

    // Show effects
    writer.writeLine('  Effects per level:', 'yellow');
    for (const effect of skill.effects) {
      const label = this.formatEffectType(effect.type);
      const statSuffix = effect.stat ? ` (${effect.stat})` : '';
      writer.writeLine(`    +${effect.valuePerLevel} ${label}${statSuffix}`, 'green');
    }

    // Show prerequisites
    if (skill.prereqs.length > 0) {
      writer.blankLine();
      writer.writeLine('  Prerequisites:', 'yellow');
      for (const prereq of skill.prereqs) {
        const prereqSkill = lookupSkill(prereq.skillId);
        const prereqLevel = state.skillLevels.get(prereq.skillId) ?? 0;
        const met = prereqLevel >= prereq.minLevel;
        const name = prereqSkill?.name ?? prereq.skillId;
        writer.writeLine(`    ${met ? '\u2713' : '\u2717'} ${name} Lv.${prereq.minLevel}`, met ? 'green' : 'red');
      }
    }

    writer.blankLine();

    if (canAlloc) {
      const options: MenuOption[] = [
        { label: 'Allocate Point', color: 'green' },
      ];
      const menuCfg = writer.menuConfig();
      const sel = await menu.show(options, {
        showExit: true,
        exitLabel: 'Back',
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
      });

      if (sel === 0) {
        ctx.skillSystem.allocate(skill.id, state);
        writer.blankLine();
        writer.writeLine(`  ${skill.name} \u2192 Level ${(level + 1)}!`, 'green');
        await effects.delay(800);
      }
    } else {
      if (level >= skill.maxLevel) {
        writer.writeLine('  MAXED', 'green');
      } else if (!ctx.skillSystem.meetsPrereqs(skill, state)) {
        writer.writeLine('  Prerequisites not met.', 'red');
      } else {
        writer.writeLine('  No available points.', 'frostSilver');
      }
      await writer.pressAnyKey();
    }
  }

  // ─── Helpers ────────────────────────────────────────────

  private buildLevelBar(current: number, max: number): string {
    const filled = '\u2588'.repeat(current);
    const empty = '\u2591'.repeat(max - current);
    return `[${filled}${empty}]`;
  }

  private formatEffectType(type: string): string {
    return type.replace(/_/g, ' ');
  }
}
